import React, { FC, useEffect } from 'react';

import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';

import { BaseStyle, BaseStyleEnums } from '@nhl/nhl-breakaway-ui';
import TranslationLoader from '@nhl/nhl-react-ui/lib/TranslationLoader';
import { useSize } from '@nhl/react-hooks/lib/useSize';

import { State } from '~/core/types';
import AdTag from '~/views/components/adTag/AdTag';
import { AdType } from '~/views/components/adTag/enums';

import { statsConfigActions } from '../../../redux/ducks/config';
import { setIntlMessages } from '../../../utility/translator';

import { StatsMenu } from './components';

import { Header, Title, Wrapper } from './styles';

const App: FC = () => {
    const dispatch = useDispatch();
    const statsConfig = useSelector((state: State) => state.config);
    const screenSize = useSize();

    const isLargeScreen = screenSize.width >= BaseStyleEnums.ScreenWidthBreakpoints.large;

    useEffect(() => {
        dispatch(statsConfigActions.requestStatsConfig());
    }, []);

    const loadTranslations = async (language) => {
        const response = await fetch(`${window.__BASE_ASSETS_URL__}lang/${language}.json?rel=${window.__APP_ID__}`);
        const translationMessages = await response.json();
        setIntlMessages(translationMessages);
        return translationMessages;
    };

    return (
        <TranslationLoader callback={loadTranslations} activeLanguage={window.__SITE_LANGUAGE_CODE__}>
            <BaseStyle.RootStyle>
                <ThemeProvider theme={BaseStyle.LightTheme}>
                    <Wrapper>
                        {isLargeScreen && <AdTag divId={window.__FULL_SIZE_AD_ID__} adType={AdType.FULL_SIZE} />}
                        <BaseStyle.RootTheming>
                            <Header>
                                {!isLargeScreen && (
                                    <AdTag divId={window.__SAP_MOBILE_AD_ID__} adType={AdType.SAP_MOBILE} />
                                )}
                                <Title>
                                    <FormattedMessage
                                        defaultMessage="Statistics"
                                        description="A page header indicating that we are looking at statistics"
                                    />
                                    {!isLargeScreen && (
                                        <AdTag
                                            divId={window.__SAP_DESKTOP_AD_ID__}
                                            adType={AdType.SAP_DESKTOP}
                                            forceReload
                                        />
                                    )}
                                </Title>
                                <StatsMenu />
                            </Header>
                            {statsConfig.loaded && <Outlet />}
                        </BaseStyle.RootTheming>
                    </Wrapper>
                </ThemeProvider>
            </BaseStyle.RootStyle>
        </TranslationLoader>
    );
};

export default App;
